import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Type, 
  QrCode, 
  Barcode, 
  Image, 
  Square, 
  Smile,
  Undo2,
  Redo2,
  Save,
  Download,
  Upload,
  Copy,
  FileJson,
  Printer,
} from 'lucide-react';
import { LabelElement } from '@/types/label';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ElementToolbarProps {
  onAddElement: (type: LabelElement['type']) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onSave?: () => void;
  onExportPdf?: () => void;
  onExportJson?: () => void;
  onImportJson?: (file: File) => void;
  onDuplicate?: () => void;
  hasSelection?: boolean;
  isMobile?: boolean;
  canManageElements?: boolean;
  canExportPdf?: boolean;
  canSaveTemplates?: boolean;
  canUseClipboard?: boolean;
  canExportJson?: boolean;
  canImportTemplates?: boolean;
}

export const ElementToolbar: React.FC<ElementToolbarProps> = ({ 
  onAddElement,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onSave,
  onExportPdf,
  onExportJson,
  onImportJson,
  onDuplicate,
  hasSelection = false,
  isMobile = false,
  canManageElements = true,
  canExportPdf = true,
  canSaveTemplates = true,
  canUseClipboard = true,
  canExportJson = true,
  canImportTemplates = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImportJson) {
      onImportJson(file);
    }
    e.target.value = '';
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-0.5 px-1.5 sm:px-2 py-1 sm:py-1.5 bg-card border-b border-border/50 overflow-x-auto scrollbar-hide">
        {/* Undo/Redo */}
        <div className="flex items-center shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                onClick={onUndo}
                disabled={!canUndo}
              >
                <Undo2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">Undo</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                onClick={onRedo}
                disabled={!canRedo}
              >
                <Redo2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">Redo</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-4 mx-1.5 shrink-0" />

        {/* Add Elements */}
        {canManageElements && (
          <div className="flex items-center shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('text')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <Type className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Text</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('qrcode')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <QrCode className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">QR Code</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('barcode')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <Barcode className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Barcode</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('image')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <Image className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Image</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('shape')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <Square className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Shape</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onAddElement('icon')}
                  className="h-8 w-8 sm:h-7 sm:w-7 text-muted-foreground hover:text-foreground touch-target"
                >
                  <Smile className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Icon</TooltipContent>
            </Tooltip>
          </div>
        )}

        {hasSelection && onDuplicate && canUseClipboard && (
          <>
            <Separator orientation="vertical" className="h-4 mx-1.5 shrink-0" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                  onClick={onDuplicate}
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Duplicate</TooltipContent>
            </Tooltip>
          </>
        )}

        <div className="flex-1 min-w-2" />

        {/* File Actions - hide some on mobile */}
        <div className="flex items-center shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />
          {!isMobile && canImportTemplates && onImportJson && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={handleImportClick}
                >
                  <Upload className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Import</TooltipContent>
            </Tooltip>
          )}
          {!isMobile && canExportJson && onExportJson && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={onExportJson}
                >
                  <FileJson className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Export JSON</TooltipContent>
            </Tooltip>
          )}
          {canSaveTemplates && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={onSave}
                >
                  <Save className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Save</TooltipContent>
            </Tooltip>
          )}

          {canExportPdf && (
            <>
              <Separator orientation="vertical" className="h-4 mx-1.5" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="h-7 px-2.5 text-xs gap-1.5"
                    onClick={onExportPdf}
                  >
                    <Printer className="w-3.5 h-3.5" />
                    Export
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">Export PDF</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
